import { Button, Tooltip } from "antd";
import { Form } from "antd";
import message from "components/CustomMessage";
import { InfoIcon } from "components/Icons";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import {
  compact,
  find,
  forEach,
  includes,
  isEmpty,
  isString,
  map,
  split,
  uniqBy,
} from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";
import { getBranchValues } from "./SonarqubeConfigListing";

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ componentName: undefined, branch: undefined }],
};

export const findingTypes = [
  {
    label: "Code Smell",
    value: "CODE_SMELL",
  },
  {
    label: "Bug",
    value: "BUG",
  },
  {
    label: "Vulnerability",
    value: "VULNERABILITY",
  },
  {
    label: "Hotspot",
    value: "HOTSPOT",
  },
];

const SonarqubeMappingFormV2 = forwardRef(function SonarqubeMappingFormV2(
  { disableProjectFields = false, ...props },
  ref,
) {
  const [form] = Form.useForm();
  const featureFlags = useFeatureFlag();
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        findingType: findingTypes.slice(2).map((type) => type.value),
        ...initialFormValues,
      },
    ];
  });

  const IntRef = useRef({});

  const [mappedComponentList] = useState(() => {
    // this state contains the already mapped application/site
    // This is stored because the project and version dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { componentName, componentDisplayName, componentNameWithKey } =
        props.record;
      const componentNameArray = componentName.split(",");
      const componentDisplayNameArray = componentDisplayName.split(",");
      const componentNameWithKeyArray = componentNameWithKey
        ? componentNameWithKey?.split(",")
        : featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY
          ? componentDisplayNameArray.map(
              (item, index) => `${item}/${componentNameArray[index]}`,
            )
          : undefined;
      return {
        componentName: componentNameArray.map((id, index) => ({
          id,
          name: featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY
            ? componentNameWithKeyArray[index]
            : componentDisplayNameArray[index],
          ...{
            otherProperties: {
              componentDisplayName: componentDisplayNameArray[index],
            },
          },
        })),
      };
    }
    return { componentName: [] };
  });

  const [options, setOptions] = useState(mappedComponentList.componentName);

  const setOptionsFromPagination = (option) => {
    setOptions((prev) => uniqBy([...prev, ...option], "id"));
  };

  const fetchComponents = useCallback(
    (params) => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          ...params,
        });
      promise
        .then((response) => {
          let { projects } = response;
          if (
            mappedComponentList["componentName"].length > 0 &&
            !isEmpty(projects)
          ) {
            projects = [...projects, ...mappedComponentList["componentName"]];
          }
          setOptionsFromPagination(projects);
        })
        .catch((err) => message.error("Problem fetching components"));
      return { promise, cancel };
    },
    [props.toolName, mappedComponentList, props.loginConfigId],
  );

  const fetchBranches = useCallback(
    (params) => {
      let { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          ...params,
        });

      promise = promise
        .then((response) => {
          const { projects } = response;
          return projects;
        })
        .catch(() => {
          message.error("Problem fetching branches");
        });

      return { promise, cancel };
    },
    [props.loginConfigId, props.toolName],
  );

  const getExtraFieldsData = useCallback(
    (config) => {
      const { projectList, findingType } = config;
      const data = projectList.reduce(
        (acc, cv) => {
          const { componentName, branch } = cv;
          const currentComponent = find(options, { id: componentName });
          const {
            name,
            otherProperties: { componentDisplayName },
          } = currentComponent;
          acc.componentName.push(componentName);
          if (featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY) {
            acc.componentDisplayName.push(componentDisplayName);
            acc.componentNameWithKey.push(name);
          } else {
            acc.componentDisplayName.push(name);
          }
          acc.componentNameWithBranch[componentName] = branch ? branch : ""
          return acc;
        },
        {
          componentNameWithBranch: {},
          componentDisplayName: [],
          componentNameWithKey: [],
          componentName: [],
        },
      );

      return {
        componentName: data.componentName.join(),
        componentNameWithBranch: JSON.stringify(data.componentNameWithBranch),
        componentDisplayName: data.componentDisplayName.join(),
        findingType: findingType?.toString(),
        ...(featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY && {
          componentNameWithKey: data.componentNameWithKey.join(),
        }),
        //send the property received in config
        ...(config.discovery_config_id && {
          discovery_config_id: config.discovery_config_id,
        }),
      };
    },
    [featureFlags, options],
  );

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { componentName, componentNameWithBranch } = config;
    const projectList = split(componentName, ",").map(
      (individualComponentName, key) => {
        if(!componentNameWithBranch){
          return {
            componentName: individualComponentName,
          };
        } else {
        const branchValues = getBranchValues(componentNameWithBranch);
        return {
          componentName: individualComponentName,
          branch: branchValues[key],
        };
      }
      },
    );
    return {
      findingType: compact(config.findingType?.split(",")),
      projectList,
      //send the property received in config
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  }, []);

  const finalKeyOptions = useCallback(
    (projects, listField) => {
      const selected = compact(
        map(
          form.getFieldsValue().configurations?.[listField.name]?.projectList,
          (p) => p?.componentName,
        ),
      );
      return map(projects, (k) => ({
        ...k,
        disabled: includes(selected, k.id),
      }));
    },
    [form],
  );

  const projectListRender = useCallback(
    (listField) => {
      const __fields = [
        {
          name: "componentName",
          colSpan: "10",
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          disabled: disableProjectFields,
          label: "Sonarqube Components",
          props: {
            api: fetchComponents,
            optionsMutator: (options) => finalKeyOptions(options, listField),
          },
          rules: [
            { required: true, message: `Sonarqube Component is required` },
          ],
        },
        {
          name: "branch",
          label: "Branch",
          colSpan: 10,
          type: FieldTypes.SELECT,
          disabled: disableProjectFields,
          props: {
            setExtraProps: (field, nestedListField) => {
              const configs = form.getFieldValue("configurations");
              const selectedComponentName =
                configs[listField.name]?.projectList[nestedListField.name]
                  ?.componentName;
              const uniqueKey = `${listField.name}${nestedListField.name}`;
              if (
              selectedComponentName &&
                (!IntRef.current[uniqueKey] ||
                  selectedComponentName !== IntRef.current[uniqueKey]?.project)
              ) {
                IntRef.current[uniqueKey] = {
                  project: selectedComponentName,
                };
              }
              return {
                api:
                  !disableProjectFields &&
                  selectedComponentName &&
                  fetchBranches,
                apiParams: IntRef.current[uniqueKey],
                resetOptionsProp: selectedComponentName,
              };
            },
          },
        },
        {
          name: "add",
          colSpan: 2,
          render: ({ remove, name, fields }) => {
            return (
              <Button
                disabled={fields.length === 1}
                type="dashed"
                onClick={() => {
                  remove(name);
                }}
                className="m-t-l"
              >
                -
              </Button>
            );
          },
        },
      ];

      return (
        <div className="m-b-sm">
          <Form.List name={[listField.name, "projectList"]}>
            {(fields, { add, remove }) => {
              return (
                <div>
                  {map(fields, (field) => {
                    return generateFormFields(
                      __fields,
                      10,
                      { ...field, add, remove, fields },
                      field.name,
                    );
                  })}
                  <Button
                    size="small"
                    type="dashed"
                    className="m-t-l"
                    onClick={() =>
                      add({
                        componentName: undefined,
                      })
                    }
                    disabled={disableProjectFields}
                  >
                    + Add Component
                  </Button>
                </div>
              );
            }}
          </Form.List>
        </div>
      );
    },
    [
      fetchComponents,
      disableProjectFields,
      fetchBranches,
      form,
      finalKeyOptions,
    ],
  );

  const extraFields = useMemo(
    () => [
      {
        name: "findingType",
        colSpan: "10",
        placeholder: "Leave blank to select all",
        disabled: disableProjectFields,
        type: FieldTypes.MULTI_SELECT,
        label: (
          <>
            Finding Type{" "}
            <Tooltip title="If none is selected, all issue types will be scanned">
              <InfoIcon className={"m-l-xs"} />
            </Tooltip>
          </>
        ),
        options: findingTypes,
        props: {
          autoFocus: !disableProjectFields,
        },
        // rules: [{ required: true, message: `Finding Type is required` }]
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender,
      },
    ],
    [projectListRender, disableProjectFields],
  );

  const onValuesChange = useCallback(
    (values, ...args) => {
      if (values.configurations) {
        let isProjectChanged = false;
        const formValues = form.getFieldsValue();
        forEach(values.configurations, (configs, index) => {
          //look into SnykMappingFormV2 for explanation
          if (
            !configs ||
            !configs.projectList ||
            isString(configs.projectList)
          ) {
            return;
          }
          forEach(configs.projectList, (object, i) => {
            //look into SnykMappingFormV2 for explanation
            if (
              object &&
              object.componentName &&
              Object.keys(object).length === 1
            ) {
              isProjectChanged = true;
              formValues.configurations[index].projectList[i].branch =
                undefined;
            }
          });
        });
        if (isProjectChanged) {
          form.setFieldsValue(formValues);
        }
      }
    },
    [form],
  );

  return (
    <GenericMappingForm
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      onValuesChange={onValuesChange}
      newRowDefaultValue={initialValues[0]}
      form={form}
      {...props}
    />
  );
});

export default SonarqubeMappingFormV2;
